import { Grid, H2 } from '@daren/ui-components'
import * as React from 'react'
import { useLocation } from 'react-router-dom'
import { useLoaderData } from 'remix'

import type { LoaderData } from './treatment-page.server'

import { FourOhFour, PortableText } from '~/components/common'
import { OptimizedImage } from '~/components/elements'
import { PricingTable } from '~/components/elements/pricing-table'
import { Container } from '~/components/layout'
import { Preview } from '~/components/utils/preview'
import { useTranslations } from '~/context/translations-provider'
import { getAllTreatments } from '~/lib/api.server'
import { getLowQualityUrlFor } from '~/lib/sanity'
import type { Handle } from '~/types'
import { getResponsiveImageSizes } from '~/utils/image'
import { pageMeta } from '~/utils/seo'

export const meta = pageMeta

export const handle: Handle = {
  id: 'treatment',
  getSitemapEntries: async () => {
    const treatments = await getAllTreatments()

    return treatments.map(page => {
      const route = `/${page.lang}/behandelingen/${page.slug}`

      return { route, priority: 0.6 }
    })
  },
}

export function CatchBoundary() {
  return <FourOhFour />
}

export default function TreatmentPage() {
  const {
    data: initialData,
    preview,
    query,
    params,
  } = useLoaderData<LoaderData>()
  const [data, setData] = React.useState(initialData)
  const { t } = useTranslations()

  const { pathname } = useLocation()

  React.useEffect(() => {
    setData(initialData)
    // on page change we need to update the data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData.slug])

  const { body, image, title, pricing, pricingEnabled } = data || {}

  React.useEffect(() => {
    setTimeout(() => {
      document.querySelector(`[href="${pathname}"]`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }, 200)
  }, [pathname])

  return (
    <>
      {preview && (
        <Preview data={data} setData={setData} query={query} params={params} />
      )}
      <div className="space-y-16 active-treatment">
        <div className="flex flex-col space-y-16 md:flex-row md:space-y-0 md:space-x-16">
          {image && (
            <div className="overflow-hidden shrink-0 w-full rounded-lg md:w-60 lg:w-72">
              <div className="w-full rounded-lg aspect-w-1 aspect-h-1">
                <OptimizedImage
                  {...image}
                  blurDataUrl={getLowQualityUrlFor(image)}
                  className="object-contain absolute inset-0 w-full h-full rounded-lg"
                  alt={title}
                  responsive={getResponsiveImageSizes('small')}
                />
              </div>
            </div>
          )}

          {body && (
            <div>
              <Grid nested className="prose">
                <Container size="full">
                  <PortableText blocks={body} />
                </Container>
              </Grid>
            </div>
          )}
        </div>
        {pricingEnabled && pricing && pricing?.length > 0 && (
          <div>
            <Grid nested>
              <Container size="full">
                <H2 className="mb-8">{t('pricing-title')}</H2>
                <PricingTable items={pricing} />
              </Container>
            </Grid>
          </div>
        )}
      </div>
    </>
  )
}
